<template>
  <div class="view-wrapper" data-cy="selling-disabled-view">
    <h1 class="title">{{ $t("compliance.sales-disabled") }}</h1>
    <p class="text">
      {{ salesDisabledText }}
    </p>
    <p class="info" data-cy="certif-error-codes">
      {{ $t("compliance.error-codes") }}: {{ errorCodes }}
    </p>
    <p class="info">{{ $t("complaince.hardware-uid") }}: {{ hardwareUid }}</p>
    <div class="retry-container">
      <SButton
        type="button"
        size="lg"
        :loading="refreshingComplianceStatus"
        @click="refreshComplianceStatus"
      >
        {{ $t("label.refresh") }}
      </SButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref, computed } from "vue";
import { preventAppRefresh, allowAppRefresh } from "@/utils/vue-helpers/log";
import { LogService } from "@/services/logging";
import {
  ComplianceErrorCodes,
  initializeComplianceLib,
  testEfrConnection,
  testFdmConnection,
} from "@/utils/helpers/compliance";
import ComplianceModule from "@/store/modules/compliance";
import { LocalStorageKeys } from "@/services/storage.service";
import { syncDeviceLocation } from "@/utils/helpers/device";
import DeviceModule from "@/store/modules/device";
import i18n from "@/lang";
import {
  isBelgianCompliant,
  isCroatianCompliant,
  isFrenchCompliant,
  isSpanishCompliant,
} from "@/utils/vue-helpers/compliance";
import { SButton } from "@smeetz/ui-core";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on i18n.global.t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
const refreshingComplianceStatus = ref(false);
const hardwareUid = computed(() => {
  return (
    DeviceModule.getHardwareHash ||
    localStorage.getItem(LocalStorageKeys.manualHardwareUuid) ||
    DeviceModule.hardwareInfo?.id ||
    "Unknown"
  );
});
const salesDisabledText = computed(() => {
  // Belgium names the FDM explicitly rather than the generic fiscal system
  if (isBelgianCompliant.value) {
    return t("compliance.sales-disabled-content-belgium");
  }
  return t("compliance.sales-disabled-content-global");
});
const errorCodes = computed(() => {
  const codes = [];
  const { packageInit, efrConnection, fdmConnection } =
    ComplianceModule.complianceStatus;
  if (!packageInit) {
    codes.push(ComplianceErrorCodes.COMPLIANCE_LIB_INIT_FAILED);
  }
  if (!fdmConnection && isBelgianCompliant.value) {
    codes.push(ComplianceErrorCodes.FDM_NOT_CONNECTED);
  }
  if (
    !efrConnection &&
    (isSpanishCompliant.value ||
      isFrenchCompliant.value ||
      isCroatianCompliant.value)
  ) {
    codes.push(ComplianceErrorCodes.EFR_NOT_CONNECTED);
  }
  if (!DeviceModule.hardwareLocation?.deviceUid) {
    codes.push(ComplianceErrorCodes.DEVICE_NOT_ASSIGNED);
  }
  if (!codes.length) {
    codes.push(ComplianceErrorCodes.UNKNWON_ERROR);
  }
  return codes.join(", ");
});

onMounted(() => {
  LogService.info("Disabled sales view mounted", { errorCodes });
  preventAppRefresh();
});

onBeforeUnmount(() => {
  LogService.info("Disabled sales view unmounted");
  allowAppRefresh();
});

async function refreshComplianceStatus() {
  refreshingComplianceStatus.value = true;
  const { packageInit, efrConnection, fdmConnection } =
    ComplianceModule.complianceStatus;
  // Check and sync device location if needed
  await syncDeviceLocation();
  if (!packageInit) {
    try {
      await initializeComplianceLib();
    } catch (error) {
      LogService.info("Failed to initialize compliance lib", { error });
    }
  }
  if (!fdmConnection && isBelgianCompliant.value) {
    try {
      await testFdmConnection();
    } catch (error) {
      LogService.info("Failed to check FDM connection", { error });
    }
  }
  if (!efrConnection) {
    try {
      await testEfrConnection();
    } catch (error) {
      LogService.info("Failed to check EFR connection", { error });
    }
  }
  refreshingComplianceStatus.value = false;
}
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;
.view-wrapper {
  position: absolute;
  width: 100%;
  height: calc(100% - 64px);
  top: 64px;
  z-index: 2005;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  .text {
    font-size: 22px;
    width: 70vw;
    margin-bottom: 3rem;
    text-align: center;
  }
  .retry-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 2rem;
  }
  .progress .el-progress--line {
    margin-top: 6rem;
    margin-bottom: 2rem;
    width: 50vw;
  }
  .taking-too-long {
    font-weight: normal;
  }
  .percentage {
    font-weight: bold;
  }
  .title {
    margin-bottom: 2rem;
    font-size: xx-large;
  }
  .info {
    text-align: center;
    margin-top: 1rem;
    font-size: 18px;
    p {
      margin: 0.25rem 0;
    }
  }
}
</style>
